import { DatePicker } from '@/design-system/components/date-picker';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileDown, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  INVOICE_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  formatMoney,
  type InvoiceDto,
  type InvoiceStatus,
} from '@cw/shared';
import { Badge, type Tone } from '@/design-system/components/badge';
import { Button } from '@/design-system/components/button';
import { Dialog, DialogClose, DialogContent, DialogFooter } from '@/design-system/components/dialog';
import { Field } from '@/design-system/components/field';
import { Input } from '@/design-system/components/input';
import { Select } from '@/design-system/components/select';
import { Sheet } from '@/design-system/components/sheet';
import { Skeleton } from '@/design-system/components/skeleton';
import { EmptyState, ErrorState } from '@/design-system/components/states';
import { api } from '@/lib/api';
import { useClients } from '@/lib/clients';
import {
  financeKeys,
  fmtDate,
  isoToday,
  moneyInput,
  parseMoney,
  useContracts,
  useInvoice,
  useInvoices,
} from '@/lib/finance';
import { handleFormError } from '@/lib/forms';
import { useCan } from '@/lib/session';

export const INVOICE_TONE: Record<InvoiceStatus, Tone> = {
  draft: 'neutral',
  issued: 'info',
  pending: 'info',
  partially_paid: 'amber',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'neutral',
};

export function InvoiceList({ invoices, onOpen }: { invoices: InvoiceDto[]; onOpen: (id: string) => void }) {
  return (
    <div className="overflow-x-auto rounded-panel shadow-[inset_0_0_0_1px_var(--color-line)]">
      <table className="w-full min-w-[640px] text-[13.5px]">
        <thead className="text-left text-[12px] text-fg-subtle">
          <tr className="border-b border-line">
            <th className="px-4 py-2.5 font-medium">Nº</th>
            <th className="px-4 py-2.5 font-medium">Cliente</th>
            <th className="px-4 py-2.5 font-medium">Vencimento</th>
            <th className="px-4 py-2.5 text-right font-medium">Total</th>
            <th className="px-4 py-2.5 text-right font-medium">Pago</th>
            <th className="px-4 py-2.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((i) => (
            <tr
              key={i.id}
              onClick={() => onOpen(i.id)}
              className="cursor-pointer border-b border-line last:border-0 hover:bg-hover"
            >
              <td className="tabular px-4 py-3 font-medium">
                <button
                  type="button"
                  className="rounded-control text-left underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-signal"
                  aria-label={`Abrir fatura ${i.number}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(i.id);
                  }}
                >
                  #{String(i.number).padStart(4, '0')}
                </button>
              </td>
              <td className="px-4 py-3">
                <span className="flex items-center gap-2">
                  <span
                    className="size-2 rounded-full"
                    style={{ background: `var(--color-label-${i.client.color})` }}
                  />
                  {i.client.name}
                </span>
              </td>
              <td className="px-4 py-3 text-fg-muted">{fmtDate(i.dueDate)}</td>
              <td className="tabular px-4 py-3 text-right">{formatMoney(i.totalCents)}</td>
              <td className="tabular px-4 py-3 text-right text-fg-muted">{formatMoney(i.paidCents)}</td>
              <td className="px-4 py-3">
                <Badge tone={INVOICE_TONE[i.status]}>{INVOICE_STATUS_LABELS[i.status]}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InvoicesTab() {
  const canWrite = useCan('finance:write');
  const [status, setStatus] = useState('all');
  const list = useInvoices({ status: status === 'all' ? undefined : status });
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={status}
          onValueChange={setStatus}
          options={[
            { value: 'all', label: 'Todas' },
            ...Object.entries(INVOICE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
          ]}
          className="w-48"
        />
        {canWrite && (
          <div className="ml-auto flex gap-2">
            <Button onClick={() => setCreating(true)}>
              <Plus /> Fatura avulsa
            </Button>
            <Button variant="primary" onClick={() => setGenerating(true)}>
              <Sparkles /> Gerar do contrato
            </Button>
          </div>
        )}
      </div>
      {list.isPending ? (
        <Skeleton className="h-64" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : !list.data.length ? (
        <EmptyState
          title="Nenhuma fatura"
          description="Gere a fatura do mês a partir de um contrato: fixo, por volume ou híbrido, com as peças entregues."
        />
      ) : (
        <InvoiceList invoices={list.data} onOpen={setOpen} />
      )}
      <InvoiceSheet id={open} onClose={() => setOpen(null)} />
      <GenerateDialog open={generating} onOpenChange={setGenerating} onCreated={setOpen} />
      <InvoiceFormDialog open={creating} onOpenChange={setCreating} onCreated={setOpen} />
    </div>
  );
}

function GenerateDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const contracts = useContracts();
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [contractId, setContractId] = useState('');
  const [start, setStart] = useState(first);
  const [end, setEnd] = useState(last);
  const gen = useMutation({
    mutationFn: () =>
      api.post<InvoiceDto>('/finance/invoices/generate', { contractId, periodStart: start, periodEnd: end }),
    onSuccess: (inv) => {
      void qc.invalidateQueries({ queryKey: financeKeys.all });
      onOpenChange(false);
      onCreated(inv.id);
      toast.success(`Fatura #${inv.number} criada como rascunho`);
    },
    onError: (e) => handleFormError(e),
  });
  const active = (contracts.data ?? []).filter((c) => c.status === 'active');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Gerar fatura do contrato"
        description="Fixo cobra o valor do contrato; volume cobra cada peça entregue no período; híbrido cobra o fixo e o que passar da franquia."
      >
        <div className="flex flex-col gap-4">
          <Field label="Contrato">
            <Select
              value={contractId}
              onValueChange={setContractId}
              placeholder="Escolha o contrato"
              options={active.map((c) => ({ value: c.id, label: `${c.client.name} · ${c.title}` }))}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="De">
              <DatePicker value={start} onValueChange={(v) => setStart(v)} className="[color-scheme:dark]" />
            </Field>
            <Field label="Até">
              <DatePicker value={end} onValueChange={(v) => setEnd(v)} className="[color-scheme:dark]" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancelar</Button>
          </DialogClose>
          <Button
            variant="primary"
            loading={gen.isPending}
            disabled={!contractId}
            onClick={() => gen.mutate()}
          >
            Gerar rascunho
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type ItemDraft = { description: string; quantity: string; price: string };

function InvoiceFormDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const clients = useClients();
  const [clientId, setClientId] = useState('');
  const [due, setDue] = useState(() => new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10));
  const [items, setItems] = useState<ItemDraft[]>([{ description: '', quantity: '1', price: '' }]);
  const total = items.reduce((a, i) => a + (parseMoney(i.price) ?? 0) * (Number(i.quantity) || 0), 0);
  const create = useMutation({
    mutationFn: () =>
      api.post<InvoiceDto>('/finance/invoices', {
        clientId,
        issueDate: isoToday(),
        dueDate: due,
        items: items
          .filter((i) => i.description.trim())
          .map((i) => ({
            description: i.description.trim(),
            quantity: Math.max(1, Number(i.quantity) || 1),
            unitPriceCents: parseMoney(i.price) ?? 0,
          })),
      }),
    onSuccess: (inv) => {
      void qc.invalidateQueries({ queryKey: financeKeys.all });
      onOpenChange(false);
      setItems([{ description: '', quantity: '1', price: '' }]);
      onCreated(inv.id);
    },
    onError: (e) => handleFormError(e),
  });
  const set = (i: number, patch: Partial<ItemDraft>) =>
    setItems((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Fatura avulsa" className="max-w-xl">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cliente">
              <Select
                value={clientId}
                onValueChange={setClientId}
                placeholder="Escolha"
                options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            </Field>
            <Field label="Vencimento">
              <DatePicker value={due} onValueChange={(v) => setDue(v)} className="[color-scheme:dark]" />
            </Field>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-[13px] font-medium text-fg-muted">Itens</p>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_64px_110px_auto] gap-2">
                <Input
                  value={it.description}
                  onChange={(e) => set(i, { description: e.target.value })}
                  placeholder="Descrição"
                />
                <Input
                  value={it.quantity}
                  onChange={(e) => set(i, { quantity: e.target.value })}
                  inputMode="numeric"
                  aria-label="Quantidade"
                />
                <Input
                  value={it.price}
                  onChange={(e) => set(i, { price: e.target.value })}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                  aria-label="Valor unitário"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Tirar item"
                  onClick={() => setItems((l) => l.filter((_, j) => j !== i))}
                  disabled={items.length === 1}
                >
                  <X />
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={() => setItems((l) => [...l, { description: '', quantity: '1', price: '' }])}
            >
              <Plus /> Item
            </Button>
          </div>
          <p className="tabular text-right text-[15px] font-semibold">{formatMoney(total)}</p>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancelar</Button>
          </DialogClose>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!clientId || !items.some((i) => i.description.trim())}
            onClick={() => create.mutate()}
          >
            Criar rascunho
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Detalhe da fatura: itens, pagamentos e ações (emitir, cancelar, registrar pagamento, PDF). */
export function InvoiceSheet({
  id,
  onClose,
  readOnly,
}: {
  id: string | null;
  onClose: () => void;
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const canWrite = useCan('finance:write') && !readOnly;
  const inv = useInvoice(id);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(isoToday());
  const [method, setMethod] = useState<string>('pix');
  const refresh = () => qc.invalidateQueries({ queryKey: financeKeys.all });
  const patch = useMutation({
    mutationFn: (body: object) => api.patch(`/finance/invoices/${id}`, body),
    onSuccess: () => void refresh(),
    onError: (e) => handleFormError(e),
  });
  const pay = useMutation({
    mutationFn: () =>
      api.post(`/finance/invoices/${id}/payments`, { amountCents: parseMoney(amount) ?? 0, paidOn, method }),
    onSuccess: () => {
      setAmount('');
      void refresh();
      toast.success('Pagamento registrado');
    },
    onError: (e) => handleFormError(e),
  });
  const delPay = useMutation({
    mutationFn: (pid: string) => api.del(`/finance/invoices/${id}/payments/${pid}`),
    onSuccess: () => void refresh(),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/finance/invoices/${id}`),
    onSuccess: () => {
      void refresh();
      onClose();
    },
    onError: (e) => handleFormError(e),
  });
  const d = inv.data;
  return (
    <Sheet
      open={Boolean(id)}
      onOpenChange={(o) => !o && onClose()}
      side="right"
      title="Fatura"
      className="w-[min(560px,100vw)]"
    >
      {!d ? (
        <div className="p-6">
          <Skeleton className="h-64" />
        </div>
      ) : (
        <div className="flex h-full flex-col">
          <header className="flex items-start gap-3 border-b border-line p-5">
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] text-fg-subtle">
                {d.client.name}
                {d.contract ? ` · ${d.contract.title}` : ''}
              </p>
              <p className="text-[20px] font-semibold">Fatura #{String(d.number).padStart(4, '0')}</p>
              <p className="mt-1 text-[12.5px] text-fg-muted">
                Emissão {fmtDate(d.issueDate)} · Vence {fmtDate(d.dueDate)}
                {d.periodStart &&
                  d.periodEnd &&
                  ` · Período ${fmtDate(d.periodStart)} a ${fmtDate(d.periodEnd)}`}
              </p>
            </div>
            <Badge tone={INVOICE_TONE[d.status]}>{INVOICE_STATUS_LABELS[d.status]}</Badge>
            <Button size="icon-sm" variant="ghost" aria-label="Fechar" onClick={onClose}>
              <X />
            </Button>
          </header>
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5">
            <table className="w-full text-[13px]">
              <tbody>
                {d.items.map((i) => (
                  <tr key={i.id} className="border-b border-line last:border-0">
                    <td className="py-2 pr-2">{i.description}</td>
                    <td className="tabular py-2 text-right text-fg-muted">
                      {i.quantity} × {formatMoney(i.unitPriceCents)}
                    </td>
                    <td className="tabular py-2 pl-3 text-right">{formatMoney(i.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tabular flex flex-col items-end gap-1 text-[13.5px]">
              {d.discountCents > 0 && (
                <p className="text-fg-muted">Desconto −{formatMoney(d.discountCents)}</p>
              )}
              <p className="text-[18px] font-semibold">{formatMoney(d.totalCents)}</p>
              <p className="text-fg-muted">
                Pago {formatMoney(d.paidCents)} · Falta {formatMoney(Math.max(0, d.totalCents - d.paidCents))}
              </p>
            </div>
            {d.payments.length > 0 && (
              <section>
                <h3 className="mb-2 text-[13.5px] font-semibold">Pagamentos</h3>
                <ul className="flex flex-col gap-1.5">
                  {d.payments.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center gap-3 rounded-[10px] bg-canvas/50 px-3 py-2 text-[13px]"
                    >
                      <span className="tabular font-medium">{formatMoney(p.amountCents)}</span>
                      <span className="text-fg-muted">
                        {fmtDate(p.paidOn)} · {PAYMENT_METHOD_LABELS[p.method]}
                      </span>
                      {canWrite && (
                        <button
                          type="button"
                          aria-label="Excluir pagamento"
                          onClick={() => delPay.mutate(p.id)}
                          className="ml-auto text-fg-subtle hover:text-danger"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {canWrite && d.status !== 'draft' && d.status !== 'cancelled' && d.status !== 'paid' && (
              <section className="flex flex-col gap-2">
                <h3 className="text-[13.5px] font-semibold">Registrar pagamento</h3>
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <Input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={moneyInput(d.totalCents - d.paidCents)}
                    inputMode="decimal"
                    aria-label="Valor"
                  />
                  <DatePicker
                    value={paidOn}
                    onValueChange={(v) => setPaidOn(v)}
                    className="[color-scheme:dark]"
                    aria-label="Data"
                  />
                  <Select
                    value={method}
                    onValueChange={setMethod}
                    options={PAYMENT_METHODS.map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
                  />
                  <Button
                    variant="primary"
                    loading={pay.isPending}
                    disabled={!parseMoney(amount || moneyInput(d.totalCents - d.paidCents))}
                    onClick={() =>
                      amount ? pay.mutate() : setAmount(moneyInput(d.totalCents - d.paidCents))
                    }
                  >
                    {amount ? 'Registrar' : 'Valor total'}
                  </Button>
                </div>
              </section>
            )}
          </div>
          <footer className="flex flex-wrap gap-2 border-t border-line p-4">
            <Button asChild>
              <a href={`/finance/invoices/${d.id}/print`} target="_blank" rel="noreferrer">
                <FileDown /> PDF
              </a>
            </Button>
            {canWrite && d.status === 'draft' && (
              <>
                <Button
                  variant="primary"
                  loading={patch.isPending}
                  onClick={() => patch.mutate({ status: 'issued' })}
                >
                  Emitir
                </Button>
                <Button variant="ghost" className="text-danger" onClick={() => remove.mutate()}>
                  Excluir rascunho
                </Button>
              </>
            )}
            {canWrite && d.status !== 'draft' && d.status !== 'cancelled' && d.paidCents === 0 && (
              <Button variant="ghost" onClick={() => patch.mutate({ status: 'cancelled' })}>
                Cancelar fatura
              </Button>
            )}
          </footer>
        </div>
      )}
    </Sheet>
  );
}
